import { useFetchProfile } from '@/hooks/useFetchProfile'
import FollowButton from './FollowButton'
import Nip05 from './Nip05'
import ProfileAbout from './ProfileAbout'
import { SimpleUserAvatar } from './UserAvatar'
import { NpubCopy } from './NpubCopy'
import { username } from '@/lib/event-metadata'
import { useAsync } from 'react-use'
import { loadRelayList } from '@nostr/gadgets/lists'
import { useAtomValue } from 'jotai'
import { relayListChannelAtom } from '@/atoms/invalidation'
import RelayIcon from './RelayIcon'
import { simplifyUrl } from '@/lib/url'
import { HugeiconsIcon } from '@hugeicons/react'
import { ChartBreakoutSquareIcon, InboxIcon } from '@hugeicons/core-free-icons'
import { NostrUser } from '@nostr/gadgets/metadata'

export default function ProfileCard({ user }: { user: string | NostrUser }) {
  const { profile } = useFetchProfile(user)
  const name = profile ? username(profile) : '<unknown>'
  const channel = useAtomValue(relayListChannelAtom)
  const { value: rl } = useAsync(
    () => (profile ? loadRelayList(profile.pubkey) : Promise.resolve(null)),
    [profile?.pubkey, channel]
  )

  if (!profile) return null

  const inboxRelays =
    rl?.items
      .filter((r) => r.read)
      .map((r) => r.url)
      .slice(0, 6) ?? []
  const outboxRelays =
    rl?.items
      .filter((r) => r.write)
      .map((r) => r.url)
      .slice(0, 6) ?? []

  return (
    <div className="w-full flex flex-col gap-2 not-prose">
      <div className="flex space-x-2 w-full items-start justify-between">
        <SimpleUserAvatar user={profile} className="w-12 h-12" />
        <FollowButton pubkey={profile.pubkey} />
      </div>
      <div>
        <div className="text-lg font-semibold truncate">{name}</div>
        <Nip05 user={profile} />
        <div className="text-xs text-muted-foreground font-mono w-full">
          <NpubCopy npub={profile.npub} />
        </div>
      </div>
      {profile.metadata.about && (
        <ProfileAbout
          about={profile.metadata.about}
          className="text-sm text-wrap break-words w-full overflow-hidden text-ellipsis line-clamp-6"
        />
      )}
      {outboxRelays && (
        <div className="flex items-center gap-1.5 mt-1 overflow-hidden" title="Outbox relays">
          <div>
            <HugeiconsIcon icon={ChartBreakoutSquareIcon} size={14} />
          </div>
          {outboxRelays.map((url) => (
            <div
              key={url}
              className="inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5"
            >
              <RelayIcon url={url} className="w-3.5 h-3.5" iconSize={8} />
              <span className="text-[10px] text-muted-foreground truncate max-w-24">
                {simplifyUrl(url)}
              </span>
            </div>
          ))}
        </div>
      )}
      {inboxRelays && (
        <div className="flex items-center gap-1.5 overflow-hidden" title="Inbox relays">
          <div>
            <HugeiconsIcon icon={InboxIcon} size={14} />
          </div>
          {inboxRelays.map((url) => (
            <div
              key={url}
              className="inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5"
            >
              <RelayIcon url={url} className="w-3.5 h-3.5" iconSize={8} />
              <span className="text-[10px] text-muted-foreground truncate max-w-24">
                {simplifyUrl(url)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
